import { useState } from 'react';

import { deleteStudy } from '../api/client.ts';

export interface DeleteStudyControlProps {
  studyId: string;
  /** The study's name, so the question names what is about to go rather than "this study". */
  title: string;
  /**
   * What the Delete button is identified by. The list has one per row and the header has one, and
   * a caller that could not say which is which would leave a test picking between them by index.
   */
  testId: string;
  /** Run after the study is gone: navigate away from it, or read the list again. */
  onDeleted: () => void;
}

/**
 * Delete, with the question in between.
 *
 * Two clicks rather than one, and the second one is a button rather than a browser confirm: a
 * study carries its whole conversation and every document drafted in it, all of which cascade,
 * and none of it comes back. The question is asked inline so the control stays keyboard-operable
 * and testable, which `globalThis.confirm` is neither.
 *
 * Whether to render this at all is the caller's decision, taken from the server's `deletable`.
 * This component does not re-derive it: the server knows about the checkout hold and the browser
 * does not, so a second opinion here could only be the wrong one.
 *
 * A refusal is still possible against an offered control — a checkout claimed between the read
 * and the click — so the server's sentence is shown verbatim rather than replaced with a generic
 * failure. It is the only place the researcher learns a delete that looked available was not.
 */
export const DeleteStudyControl = ({
  studyId,
  title,
  testId,
  onDeleted,
}: DeleteStudyControlProps) => {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!confirming) {
    return (
      <span className="delete-study">
        {error !== null && (
          <span className="chat-error" data-test-id={`${testId}-error`} role="alert">
            {error}
          </span>
        )}
        <button
          type="button"
          data-test-id={testId}
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
        >
          Delete
        </button>
      </span>
    );
  }

  return (
    <span className="delete-study">
      <span data-test-id="delete-study-question">Delete “{title}” and everything in it?</span>
      <button
        type="button"
        data-test-id="confirm-delete"
        disabled={deleting}
        onClick={() => {
          setDeleting(true);
          setError(null);
          void deleteStudy(studyId).then(
            () => {
              // No state is cleared first: the caller navigates or re-reads, and unmounting this
              // is what ends it. Setting state on the way out would be a write after unmount.
              onDeleted();
            },
            (failure: unknown) => {
              setDeleting(false);
              setConfirming(false);
              setError(
                failure instanceof Error ? failure.message : 'The study could not be deleted.',
              );
            },
          );
        }}
      >
        {deleting ? 'Deleting…' : 'Delete study'}
      </button>
      <button
        type="button"
        data-test-id="cancel-delete"
        disabled={deleting}
        onClick={() => setConfirming(false)}
      >
        Keep it
      </button>
    </span>
  );
};
